import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { EditorState, Modifier } from "draft-js";
import { useTranslation } from "react-i18next";
import toaster from "composants/toaster/toaster";
import { NotificationGroup } from "types/Notification";
import ToolbarButton from "../ToolbarButton";
import { PluginObject, PluginToolbarProps } from "./pluginTypes";

type BrowserSpeechRecognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
};

type BrowserSpeechRecognitionEvent = {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

function getSpeechRecognitionConstructor(): BrowserSpeechRecognitionConstructor | null {
  const w = window as Window & {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

function resolveSpeechLang(language: string | undefined): string {
  if (!language) return "fr-FR";
  if (language.includes("-")) return language;
  if (language.startsWith("en")) return "en-US";
  if (language.startsWith("fr")) return "fr-FR";
  return language;
}

function insertTextAtCursor(editorState: EditorState, text: string): EditorState {
  const selection = editorState.getSelection();
  const content = Modifier.replaceText(editorState.getCurrentContent(), selection, text);
  const next = EditorState.push(editorState, content, "insert-characters");
  return EditorState.forceSelection(next, content.getSelectionAfter());
}

const SpeechDictationToolbar: FC<PluginToolbarProps> = ({
  editorState,
  setEditorState,
  focusEditor,
  disabled,
  readonly,
  language
}) => {
  const { t } = useTranslation();
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const wantListeningRef = useRef(false);
  const editorStateRef = useRef(editorState);
  const [isListening, setIsListening] = useState(false);

  editorStateRef.current = editorState;

  const speechSupported =
    typeof window !== "undefined" && !!getSpeechRecognitionConstructor();
  const speechDisabled = !!readonly || !!disabled;

  const stopSpeechRecognition = useCallback(() => {
    wantListeningRef.current = false;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // déjà arrêté
      }
    }
    setIsListening(false);
  }, []);

  const insertSpeechTranscript = useCallback(
    (transcript: string) => {
      const trimmed = transcript.trim();
      if (!trimmed) return;

      const current = editorStateRef.current;
      const selection = current.getSelection();
      const block = current.getCurrentContent().getBlockForKey(selection.getStartKey());
      const offset = selection.getStartOffset();
      const charBefore = offset > 0 ? block.getText().charAt(offset - 1) : "";
      const needsSpace = !!charBefore && !/\s/.test(charBefore);
      const textToInsert = needsSpace ? ` ${trimmed}` : trimmed;

      setEditorState(insertTextAtCursor(current, textToInsert));
    },
    [setEditorState]
  );

  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
    if (!SpeechRecognitionCtor) {
      toaster.notify({
        id: "speech-unsupported",
        group: NotificationGroup.DEFAULT,
        priority: "NORMAL",
        intent: "WARNING",
        title: t("commun_dictée_non_supportee", {
          defaultValue: "La dictée vocale n'est pas supportée par ce navigateur"
        }),
        createdAt: new Date().toISOString()
      });
      return;
    }

    stopSpeechRecognition();

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = resolveSpeechLang(language);
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onresult = event => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        insertSpeechTranscript(transcript);
      }
    };

    recognition.onerror = event => {
      if (event.error === "aborted" || event.error === "no-speech") {
        return;
      }
      wantListeningRef.current = false;
      setIsListening(false);
      recognitionRef.current = null;
      if (event.error === "not-allowed") {
        toaster.notify({
          id: "speech-permission",
          group: NotificationGroup.DEFAULT,
          priority: "NORMAL",
          intent: "WARNING",
          title: t("commun_dictée_permission", {
            defaultValue: "Autorisez l'accès au microphone pour dicter"
          }),
          createdAt: new Date().toISOString()
        });
      }
    };

    recognition.onend = () => {
      // Chrome coupe souvent après un silence : on relance si l'utilisateur est toujours en dictée
      if (wantListeningRef.current && recognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch {
          stopSpeechRecognition();
        }
      } else {
        setIsListening(false);
        if (recognitionRef.current === recognition) {
          recognitionRef.current = null;
        }
      }
    };

    recognitionRef.current = recognition;
    wantListeningRef.current = true;
    setIsListening(true);
    focusEditor?.();

    try {
      recognition.start();
    } catch {
      stopSpeechRecognition();
    }
  }, [focusEditor, insertSpeechTranscript, language, stopSpeechRecognition, t]);

  const toggleSpeechRecognition = useCallback(() => {
    if (speechDisabled) return;
    if (isListening) {
      stopSpeechRecognition();
    } else {
      startSpeechRecognition();
    }
  }, [isListening, speechDisabled, startSpeechRecognition, stopSpeechRecognition]);

  useEffect(() => {
    if (speechDisabled && isListening) {
      stopSpeechRecognition();
    }
  }, [isListening, speechDisabled, stopSpeechRecognition]);

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      try {
        recognitionRef.current?.abort();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    };
  }, []);

  return (
    <>
      <span
        style={{
          display: "inline-block",
          width: 1,
          height: "1.5em",
          background: "#dedede",
          margin: "0 0.5em",
          verticalAlign: "middle"
        }}
      />
      <ToolbarButton
        icon="microphone"
        isActive={isListening}
        disabled={speechDisabled || !speechSupported}
        title={
          !speechSupported
            ? t("commun_dictée_non_supportee", {
                defaultValue: "La dictée vocale n'est pas supportée par ce navigateur"
              })
            : isListening
              ? t("commun_dictée_arreter", { defaultValue: "Arrêter la dictée" })
              : t("commun_dictée_demarrer", { defaultValue: "Dicter (micro)" })
        }
        onClick={toggleSpeechRecognition}
      />
    </>
  );
};

/**
 * Plugin de dictée vocale (Web Speech API).
 * Ajoute un bouton micro dans la toolbar de l'éditeur.
 */
export default function speechDictationPlugin(): PluginObject {
  return {
    Toolbar: SpeechDictationToolbar
  };
}
